/**
 * Sample Registration — domain model, lookup catalogs and pure helpers.
 *
 * Ported from the approved design prototype
 * `29131-AI_Sample Registration/Sample-Tracking-Module-Prototype.html`
 * (2026-07-18), which mirrors the legacy XAF view
 * `Samplecheckin_DetailView_Copy_SampleRegistration`.
 *
 * ── SCOPE OF THIS FILE ──────────────────────────────────────────────────────
 * Types, constants, and pure helpers for the Sample Registration form.
 * Lookups are loaded from `AI_SampleRegistration_Lookups_SP` via the real API.
 * Bottle grouping / hold time / TAT→due date are reproduced from legacy so the
 * form behaves correctly on screen; the server re-derives them at save time
 * (CLAUDE.md §1 "all business logic lives in the API").
 */

// ─────────────────────────────────────────────────────────────────────────────
// Model
// ─────────────────────────────────────────────────────────────────────────────

/** Per-bottle detail captured in the BottleID picker (one physical container).
 *  Maps to dbo.SampleBottleAllocation columns: Containers / Preservative /
 *  StorageID / StorageCondition.  pH / Temperature / PhPaperID / ThermometerID
 *  belong to the SCC (Sample Condition Check) tab, NOT to bottle allocation. */
export interface SrBottleDetail {
  container: string;
  preserv: string;
  storage: string;
  storageCondition: string;
}

/** Per-parameter (analyte) result row — only FIELD methods are keyed here; lab
 *  methods get their results later in Result Entry. */
export interface SrParamData {
  result: string;
  units: string;
  analysisDate: string;
  analyzedBy: string;
}

/** One test assignment on one sample (one sample → many tests). */
export interface SrTest {
  /** Stable client-side key — grids need an id that survives re-ordering. */
  key: string;
  name: string;
  method: string;
  /** Analytical component / variant ("Default", "Watershed", …). */
  comp: string;
  /** Parent group name for this test (empty string if none). */
  testGroupName: string;
  /** Assigned bottle letter(s), comma-joined ("A" or "A,B"). */
  bottle: string;
  container: string;
  preserv: string;
  storage: string;
  /** Per-bottle detail, lazily built by the bottle picker. */
  bd: Record<string, SrBottleDetail> | null;
  /** Analyte → included?  null = every analyte included (AllParam). */
  params: Record<string, boolean> | null;
  /** Analyte names saved for this test on a RELOADED job (one SampleParameter row each).
   *  Set by the load mapper; SampleLoginView expands it into `params` once the test's
   *  full analyte list is known (a subset → Customized), then clears it. */
  savedParamNames?: string[];
  /** Analyte → field-method result row. */
  paramData: Record<string, SrParamData>;
  hdTime: string;
  status: string;
  remark: boolean;
  subOut: boolean;
  added: string;
  hold: boolean;
  /** TestMethod.Oid (UUID) as loaded from DB — used as testOid on save to
   *  bypass the display-name lookup for DB-loaded tests. Undefined for
   *  newly-added tests (they resolve via oidByLabel as before). */
  testMethodOid?: string;
  /** TurnAroundTime.Oid as loaded from dbo.SampleParameter — used as tatOid
   *  on save when no customRequest override exists for this test. */
  tatOid?: string | null;
  /** True when TestMethod.IsFieldTest = 1 in the DB — field tests allow inline
   *  result entry here; lab tests get results in Result Entry. */
  isFieldTest?: boolean;
  /** True when this test was LOADED from the DB WITH a persisted
   *  dbo.SampleBottleAllocation (round-cause A hydration). Its bottle / container /
   *  preservative are AUTHORITATIVE, so the single-default guide auto-fill and the
   *  auto bottle-regrouping (autoBottles) must NOT touch it — legacy
   *  AssignBottleAllocationToSamples is incremental / non-destructive
   *  (round-cause B). Undefined for a new test (mkTest) and cleared on clone. */
  hasSavedBottle?: boolean;
}

/** One sample (one row of the Sample Login grid). */
export interface SrSample {
  /** {JobID}-NNN — minted from the job id + ordinal. */
  id: string;
  hold: boolean;
  /** The wide Sample Login column set, keyed by SL_COLS name. */
  v: Record<string, string>;
  tests: SrTest[];
}

/** A receipt event — one Job ID can receive samples several times. */
export interface SrReceivedDate {
  key: string;
  /** Server Oid (dbo.SC_ReceivedDates.Oid); '' for a new, unsaved row. Round-
   *  trips so a later save UPDATEs the row instead of duplicating it. */
  oid: string;
  date: string;
  by: string;
  relinq: string;
  n: number;
  matrix: string[];
  tat: string;
  cooler: string;
  cat: string[];
  createdBy: string;
  created: string;
}

export interface SrAttachment {
  key: string;
  /** Server Oid (dbo.Attachment.Oid); '' for a new, unsaved row. */
  oid: string;
  name: string;
  cat: string;
  file: string;
  /** FileData FK Oid — links to dbo.FileData. */
  fileDataOid: string;
  date: string;
  op: string;
  /** Operator Employee Oid — for FK resolution on save. */
  opOid: string;
  recv: string;
  comment: string;
  /** Pending File object for upload — only present on NEW rows before save. */
  fileObj?: File;
}

/** Category int → display label (matches XAF Category enum). */
export const ATT_CAT_LABELS: Record<number, string> = {
  0: 'COC', 1: 'Photo', 2: 'Report', 3: 'ResultEDD', 4: 'Subout', 5: 'Other',
};

/** Display label → category int. */
export const ATT_CAT_VALUES: Record<string, number> = {
  COC: 0, Photo: 1, Report: 2, ResultEDD: 3, Subout: 4, Other: 5,
};

/** The subset of categories shown in the New/Edit dropdown. */
export const ATT_CAT_OPTIONS = ['COC', 'Report', 'Photo', 'Other'] as const;

/** Sample Condition Check — one per receipt event. */
export interface SrSccRecord {
  key: string;
  /** Server Oid (dbo.SampleConditionCheck.Oid); '' for a new, unsaved row. */
  oid: string;
  matrix: string;
  by: string;
  date: string;
  recv: string;
  temp: string;
  corr: string;
  thermo: string;
  ph: string;
  paper: string;
  /** Per-check-point answer, index-aligned to SCC_POINTS. */
  points: string[];
  /** Per-check-point description, index-aligned to SCC_POINTS. */
  descriptions: string[];
  comments: SrSccComment[];
}

export interface SrSccComment { text: string; user: string; dt: string }

export interface SrNote {
  key: string;
  /** Server Oid (dbo.Notes.Oid); '' for a new, unsaved row. */
  oid: string;
  title: string;
  author: string;
  /** The author's Employee Oid — used for author-only enforcement. */
  authorOid: string;
  date: string;
  sort: number;
  html: string;
  /** Plain-text version of the note (strip tags). */
  plainText: string;
  /** SourceID — links note to the job. */
  sourceId: string;
  /** NoteSource — e.g. "Sample Registration". */
  noteSource: string;
}

export interface SrItemCharge {
  key: string;
  /** Server Oid (dbo.SampleCheckinItemChargePricing.Oid); '' for a new, unsaved row. */
  oid: string;
  /** ItemChargePricing.Oid — sent as @itemPrice to the save SP so the FK is set correctly. */
  itemPriceOid: string;
  /** ItemChargePricing.ItemName — display value for the ItemName grid column. */
  name: string;
  /** Per-job freetext description entered by the user (SampleCheckinItemChargePricing.Description). */
  desc: string;
  price: number;
  qty: number;
  amount: number;
  final: number;
  /** Non-preferred unit price (NpUnitPrice). */
  npPrice: number;
  disc: number;
  remark: string;
}

export interface SrImage {
  key: string;
  /** Server Oid (dbo.Image.Oid); '' for a new, unsaved row. */
  oid: string;
  name: string;
  photo: string;
  /** dbo.FileData.Oid for the uploaded image binary. */
  fileDataOid: string;
  sort: number;
  comment: string;
}

/** Every child collection the Registration bottom tabs own. */
export interface SrTabState {
  receivedDates: SrReceivedDate[];
  attachments: SrAttachment[];
  sccRecords: SrSccRecord[];
  notes: SrNote[];
  itemCharges: SrItemCharge[];
  images: SrImage[];
}

export function emptyTabState(): SrTabState {
  return { receivedDates: [], attachments: [], sccRecords: [], notes: [], itemCharges: [], images: [] };
}

/** Every value the Registration step's three field sections hold. */
export interface SrHeader {
  jobId: string;
  receivedDate: string;
  receivedBy: string;
  createdBy: string;
  client: string;
  /** The chosen client's exact Oid, captured at pick time. Drives the
   *  client-scoped Project / Collector / Contact / Quote lookups without
   *  ambiguity when two customers share a name. */
  clientOid: string;
  clientAddress: string;
  clientPhone: string;
  clientContact: string;
  accountNumber: string;
  quoteId: string;
  poNumber: string;
  paymentStatus: string;
  manifestNumber: string;
  projectManager: string;
  dateExpect: string;
  relinquishedBy: string[];
  collector: string[];
  dateCollected: string;
  timeCollected: string;
  projectId: string;
  /** The chosen project's exact Oid, straight from the detail read (mirrors
   *  clientOid) — never re-resolved through the client-scoped lookups list,
   *  which is empty/stale while it reloads on client or matrix change. */
  projectOid: string;
  projectName: string;
  projectLocation: string;
  permit: string;
  projectCategory: string;
  cocId: string;
  invoiceContact: string;
  registrationType: string;
  jobInactivation: string;
  cancelJob: boolean;
  cancelStatus: number;
  /** Samplecheckin.Status sign-off number (0 = Pending Submit … 4 = Submitted). */
  signoffStatus: number;
  cocSource: string;
  prevStatusId: string;
  status: string;
  /** Sample Information — Sample Matrix is a CheckedComboBox (multi-select). */
  matrices: string[];
  /** Header-level test template (CheckedComboBox) — every generated sample
   *  starts from this set and is customised per sample in Test Assignment. */
  // Values are the XAF composite "TestName|MethodNumber" (e.g.
  // "Ammonia_Free|HACH 10200") so two same-named tests with different methods
  // are distinct + uniquely resolvable to their TestMethod Oid.
  testTemplate: string[];
  /** Composite "TestName|MethodNumber" → method, remembered from the picker rows. */
  testTemplateMethods: Record<string, string>;
  sampleCategory: string[];
  coolerId: string;
  noOfSamples: string;
  remark: string;
  /** Report Requirements. */
  tat: string;
  dueDate: string;
  reportTemplate: string;
  eddTemplate: string;
  eddFormat: string;
  reportDeliveryAddress: string;
  reportDeliveryMethod: string;
}

/** A blank header for a New registration. */
export function emptyHeader(registrationType: string, now: string, user: string): SrHeader {
  return {
    jobId: '', receivedDate: now, receivedBy: user, createdBy: user,
    client: '', clientOid: '', clientAddress: '', clientPhone: '', clientContact: '',
    accountNumber: '',
    quoteId: '', poNumber: '', paymentStatus: '', manifestNumber: '0',
    projectManager: '', dateExpect: '', relinquishedBy: [], collector: [],
    dateCollected: '', timeCollected: '', projectId: '', projectOid: '', projectName: '',
    projectLocation: '', permit: '', projectCategory: '', cocId: '',
    invoiceContact: '', registrationType, jobInactivation: '',
    cancelJob: false, cancelStatus: 0, signoffStatus: 0, cocSource: '', prevStatusId: '',
    status: 'Pending Saving',
    matrices: [], testTemplate: [], testTemplateMethods: {},
    sampleCategory: [], coolerId: '', noOfSamples: '1', remark: '',
    tat: '', dueDate: '', reportTemplate: '', eddTemplate: '', eddFormat: '',
    reportDeliveryAddress: '', reportDeliveryMethod: '',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Lookup catalogs (prototype reference data — see the file header)
// ─────────────────────────────────────────────────────────────────────────────

/** One grid-picker lookup: column captions + rows (column 0 is the DISPLAY
 *  value shown in the closed field). `oidByLabel` maps the display value to
 *  the FK Oid the API needs at Save time — populated from Col5 of the SP
 *  payload. Absent (empty) for text/enum lookups (Report Template, EDD
 *  Export Format) where the display value IS what gets persisted. */
export interface SrLookup {
  cols: string[];
  rows: string[][];
  oidByLabel?: Record<string, string>;
}

/** Resolve a display value (as stored in SrHeader) to the FK Oid the API
 *  expects. Returns null when the lookup has no Oid map (text/enum) or the
 *  label was never picked from a lookup row. Callers should use `?? null`
 *  to send NULL for optional FKs. */
export function resolveLookupOid(lookup: SrLookup, label: string): string | null {
  if (!label) return null;
  return lookup.oidByLabel?.[label] ?? null;
}

/** Every lookup key the Registration form opens a grid picker for. */
export type SrLookupKey =
  | 'client' | 'project' | 'contact' | 'invoicecontact' | 'account' | 'quote' | 'matrix' | 'test' | 'tat'
  | 'payment' | 'pm' | 'category' | 'sampleCategory' | 'permit' | 'cocid' | 'inact' | 'edd'
  | 'rpt' | 'eddtpl' | 'employee' | 'relinq' | 'collector' | 'unit';

export const LOOKUPS: Record<SrLookupKey, SrLookup> = {
  // Column metadata stays local; rows are loaded from
  // GET /api/v1/sample-registration/lookups.
  client: { cols: ['Client', 'Address', 'Phone'], rows: [] },
  project: { cols: ['Project ID', 'Project Name', 'Location'], rows: [] },
  // G5 parity (user request 2026-09-26): legacy ContactName / InvoiceContact are
  // single-column AllItems combos with DIFFERENT backend filters —
  // Client Contact = report contacts (IsReport=1), Invoice Contact = invoice
  // contacts (IsInvoice=1). Split into two kinds so each shows the right list;
  // both single-column (name only, no Email column).
  contact: { cols: ['Contact'], rows: [] },
  invoicecontact: { cols: ['Contact'], rows: [] },
  // Account Number — the selected client's non-suspended accounts
  // (dbo.ClientAccountNumber), client-scoped like Contact. Legacy parity.
  account: { cols: ['Account Number'], rows: [] },
  quote: { cols: ['Quote ID', 'Title', 'Project ID', 'Project Name'], rows: [] },
  matrix: { cols: ['Sample Matrix'], rows: [] },
  test: { cols: ['Test', 'Method', 'Group'], rows: [] },
  tat: { cols: ['TAT'], rows: [] },
  payment: { cols: ['Payment Status'], rows: [] },
  pm: { cols: ['Project Manager'], rows: [] },
  category: { cols: ['Project Category'], rows: [] },
  // "Sample Category" is a SEPARATE aggregate — dbo.SampleCategory
  // (SampleCategoryName), distinct from Project Category which lives in
  // dbo.ProjectCategory.  The legacy Samplecheckin.SampleCategory column
  // stores a ';'-joined list of these Oids (multi-select).
  sampleCategory: { cols: ['Sample Category'], rows: [] },
  // Permit = multi-column grid-lookup (GoLive parity, user request 2026-09-24):
  // ID · PermitType · Name · Number. The stored value stays PermitName (Name, col
  // index 2) — see mapLookupRows (oidByLabel keyed by Name) + applyPick + the
  // permit rowValue in RegistrationForm — so Save/Get are unchanged.
  permit: { cols: ['ID', 'PermitType', 'Name', 'Number'], rows: [] },
  cocid: { cols: ['COC ID'], rows: [] },
  inact: { cols: ['Action', 'Inactivation Reason'], rows: [] },
  // EDD Export Format is NOT an FK — it's the enum `EDDFormat` on the legacy
  // Samplecheckin BO, persisted as an INT in Samplecheckin.EDDFormat. The rows
  // here are the display labels; EDD_FORMAT_TO_INT maps them to the enum value.
  edd: { cols: ['EDD Export Format'], rows: [
    ['Export to Excel All Sheet'], ['Export to Excel Single Sheet'],
    ['Export to CSV'], ['Export to TXT'], ['Export to XML'],
  ] },
  rpt: { cols: ['Report Template'], rows: [] },
  eddtpl: { cols: ['EDD Template'], rows: [] },
  // "Received By" — fed by the GLOBAL employee lookup (GET /api/v1/lookups/employees
  // → dbo.AI_Employee_Lookup_SP), NOT by the Sample Registration lookups SP.
  // ONE column: dbo.Employee.DisplayName verbatim, already sorted ASC by the SP.
  employee: { cols: ['Employee'], rows: [] },
  relinq: { cols: ['Relinquished By'], rows: [] },
  collector: { cols: ['Collector'], rows: [] },
  unit: { cols: ['Unit'], rows: [] },
};

/** Lookups that support the inline "＋ New {record}" quick-add (legacy parity:
 *  "Select a client from the dropdown; Allow to add a new one"). */
export const NEWABLE: Partial<Record<SrLookupKey, string>> = {
  client: 'Client', contact: 'Contact', project: 'Project',
  category: 'Project Category', pm: 'Project Manager', payment: 'Payment Status',
  inact: 'Job Inactivation', permit: 'Permit', cocid: 'COC ID',
  relinq: 'Relinquished By', collector: 'Collector',
};

/** CheckedComboBox lookups — tick several, popup stays open (legacy parity). */
export const MULTI_LOOKUPS: SrLookupKey[] = ['matrix', 'test', 'relinq', 'collector', 'sampleCategory'];

/**
 * EDD Export Format — the legacy `EDDFormat` enum on Samplecheckin.cs
 * (persisted as INT in dbo.Samplecheckin.EDDFormat). Order/values are the enum's
 * implicit ints, verified against the BO:
 *   NA=0 · ExporttoExcelAllSheet=1 · ExportToExcelSingleSheet=2 ·
 *   ExporttoCSV=3 · ExporttoTXT=4 · ExporttoXML=5
 * The picker stores the DISPLAY label; these maps convert display ↔ enum int
 * for save (toInt) and round-trip load (fromInt).
 */
export const EDD_FORMAT_TO_INT: Record<string, number> = {
  'Export to Excel All Sheet': 1,
  'Export to Excel Single Sheet': 2,
  'Export to CSV': 3,
  'Export to TXT': 4,
  'Export to XML': 5,
};
export const EDD_INT_TO_FORMAT: Record<number, string> = {
  1: 'Export to Excel All Sheet',
  2: 'Export to Excel Single Sheet',
  3: 'Export to CSV',
  4: 'Export to TXT',
  5: 'Export to XML',
};

/** Display label → EDDFormat enum int (null for N/A / unknown). */
export function eddFormatToInt(label: string | undefined): number | null {
  if (!label) return null;
  return EDD_FORMAT_TO_INT[label] ?? null;
}
/** EDDFormat enum int (or its string form from the API) → display label. */
export function eddFormatFromInt(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(n) ? String(value) : (EDD_INT_TO_FORMAT[n] ?? '');
}

/** TAT → turnaround in DAYS.  Due Date = Received Date + TAT (legacy rule;
 *  the API must own this once the save endpoint lands). */
export const TAT_DAYS: Record<string, number> = {
  '1 Day': 1, '3 Days': 3, '5 Days': 5, '10 Days': 10,
};

/** Test-assignment catalog — [Test, Method, Component, IsGroup, TestGroup]. */
export const TA_CATALOG: readonly (readonly [string, string, string, string, string])[] = [
  ['Colilert', 'SM 9223B', 'Default', 'No', ''],
  ['Total Coliform', 'SM 9223B', 'Default', 'No', ''],
  ['E. coli', 'SM 9223B', 'Default', 'No', ''],
  ['Free Ammonia', 'Field Method', 'Default', 'No', ''],
  ['Free Residual Chlorine', 'Field Method', 'Default', 'No', ''],
  ['Monochloramine', 'Field Method', 'Default', 'No', ''],
  ['Total Residual Chlorine', 'Field Method', 'Default', 'No', ''],
  ['Metals (36)', 'EPA 200.8', 'Default', 'Yes', ''],
  ['Nitrate', 'EPA 300.0', 'Default', 'No', ''],
  ['PFAS EPA 1633', 'EPA 1633', 'Default', 'No', 'PFAS 1633'],
  ['PFAS EPA 1633', 'EPA 1633', 'Watershed', 'No', 'PFAS 1633'],
  ['PFAS_533', 'EPA 533', 'Default', 'No', ''],
  ['PFAS_533', 'EPA 533', 'Watershed', 'No', ''],
  ['Turbidity', 'EPA 180.1', 'Default', 'No', ''],
  ['pH', 'SM 4500-H+', 'Default', 'No', ''],
];

const PFAS_PARAMS = [
  '11Cl-PF3OUdS', '4:2 FTS', '6:2 FTS', '8:2 FTS', '9Cl-PF3ONS', 'ADONA', 'HFPO-DA',
  'NFDHA', 'PFBA', 'PFBS', 'PFDA', 'PFDoA', 'PFDS', 'PFEESA', 'PFHpA', 'PFHpS',
  'PFHxA', 'PFHxS', 'PFMBA', 'PFMPA', 'PFNA', 'PFOA', 'PFOS', 'PFPeA', 'PFUnA',
];

/** Test → its analyte (parameter) list.  A test not listed here has exactly one
 *  analyte with the same name. */
export const PARAMS: Record<string, string[]> = {
  'Metals (36)': ['Arsenic', 'Barium', 'Cadmium', 'Chromium', 'Copper', 'Iron', 'Lead',
    'Manganese', 'Mercury', 'Nickel', 'Selenium', 'Silver', 'Zinc'],
  'PFAS_533': PFAS_PARAMS,
  'PFAS EPA 1633': PFAS_PARAMS,
  'Total Coliform': ['Total Coliform'],
  'E. coli': ['E. coli'],
  'Colilert': ['Total Coliform', 'E. coli'],
  'Nitrate': ['Nitrate (as N)'],
  'Free Ammonia': ['Free Ammonia (as N)'],
  'Free Residual Chlorine': ['Free Residual Chlorine'],
  'Monochloramine': ['Monochloramine'],
  'Total Residual Chlorine': ['Total Residual Chlorine'],
  'Turbidity': ['Turbidity'],
  'pH': ['pH'],
};

/** Every analyte for a test (single-analyte tests fall back to their own name). */
export function paramsOf(testName: string): string[] {
  return PARAMS[testName] ?? [testName];
}

/** Per-test default container + preservative, pre-filled in the bottle picker. */
const TEST_DEFAULTS: Record<string, { container: string; preserv: string }> = {
  'Adjusted Gross Alpha': { container: '1 L Cube, Plastic', preserv: 'HNO3' },
  'Total Coliform': { container: '250 mL Sterile, Plastic', preserv: 'Na2S2O3' },
  'Colilert': { container: '250 mL Sterile, Plastic', preserv: 'Na2S2O3' },
  'E. coli': { container: '250 mL Sterile, Plastic', preserv: 'Na2S2O3' },
  'Metals (36)': { container: '500 mL, Plastic', preserv: 'HNO3' },
  'Nitrate': { container: '250 mL, Plastic', preserv: 'H2SO4' },
  'PFAS_533': { container: '250 mL HDPE', preserv: 'Trizma' },
  'PFAS EPA 1633': { container: '250 mL HDPE', preserv: 'None' },
  'Free Ammonia': { container: '250 mL, Plastic', preserv: 'H2SO4' },
  'Free Residual Chlorine': { container: 'Field', preserv: 'None' },
  'Monochloramine': { container: 'Field', preserv: 'None' },
  'Total Residual Chlorine': { container: 'Field', preserv: 'None' },
  'Turbidity': { container: '100 mL, Plastic', preserv: 'None' },
  'pH': { container: 'Field', preserv: 'None' },
};

export function testDefaults(name: string): { container: string; preserv: string } {
  // No invented fallback (was '250 mL, Plastic' / 'None' for EVERY unlisted test — bug 17): a test
  // with no Test Guide row stays blank (N/A) until the user picks; Test Guide rows fill it in.
  return TEST_DEFAULTS[name] ?? { container: '', preserv: '' };
}

/**
 * The auto-default for a bottle field (Container / Preservative) from a test's
 * TestGuide rows — the FIRST (top) distinct value in Test Guide order.
 *
 * ALPACA (`SampleRegistrationViewController.addnewtestbottles`) auto-fills ONLY
 * when the test has exactly ONE distinct container/preservative and otherwise
 * leaves it blank. Owner decision (2026-09-30): AI instead pre-fills the TOP
 * value even when the test guide lists several, so a new test never lands with a
 * blank Container/Preservative — the user can still change it from the combo,
 * which offers every allowed value. `guideSets` inserts in Test Guide order and
 * RS5 of AI_SampleRegistration_GetBottleLookups is ORDER BY tg.Oid, so the first
 * element is the top Test Guide row (the same row the ALPACA Test Guide grid
 * shows first).
 *   · one or more distinct values → the FIRST one (auto-load, user-editable)
 *   · no TestGuide rows           → undefined (caller keeps its own fallback)
 */
export function guideFirstDefault(allowed: Set<string> | undefined): string | undefined {
  if (!allowed || allowed.size === 0) return undefined;
  return [...allowed][0]!;
}

/** Per-TestMethod allowed Container / Preservative name sets, built from the
 *  TestGuide lookup rows (RS5 of the bottle-lookups API). */
/** Map keyed by a Guid string, case-insensitively. The bottle-lookups SP returns the
 *  TestMethod Oid UPPER-case (CAST(guid AS NVARCHAR)) while a saved test carries it
 *  lower-case (Guid.ToString), so a plain Map never matched and the per-test
 *  Container / Preservative filter silently fell back to EVERY container (bug 17). */
export class GuidKeyMap<V> extends Map<string, V> {
  override get(k: string): V | undefined { return k == null ? undefined : super.get(k.toLowerCase()); }
  override has(k: string): boolean { return k == null ? false : super.has(k.toLowerCase()); }
  override set(k: string, v: V): this { return super.set((k ?? "").toLowerCase(), v); }
}

/** The Preservative the Test Guide pairs with `container` for this test method —
 *  a Test Guide row IS a (Container, Preservative) pair, so picking the container
 *  decides the preservative. Undefined when the guide has no preservative for it. */
export function guidePreservFor(
  testGuide: TestGuideLookup[], testMethodOid: string | undefined, container: string,
): string | undefined {
  if (!testMethodOid || !container) return undefined;
  const k = testMethodOid.toLowerCase();
  // The API can return a Test Guide row with no TestMethod (a null FK) — skip it instead of
  // throwing, which blanked the whole page on a Test Assignment save (2026-10-13).
  return testGuide.find(r => (r.testMethodOid ?? '').toLowerCase() === k
    && r.containerName === container && r.preservativeName)?.preservativeName ?? undefined;
}

export function guideSets(testGuide: TestGuideLookup[]): {
  containers: Map<string, Set<string>>; preservatives: Map<string, Set<string>>;
} {
  const containers = new GuidKeyMap<Set<string>>();
  const preservatives = new GuidKeyMap<Set<string>>();
  const add = (m: Map<string, Set<string>>, oid: string, v: string) => {
    let s = m.get(oid);
    if (!s) { s = new Set(); m.set(oid, s); }
    s.add(v);
  };
  for (const row of testGuide) {
    if (row.containerName) add(containers, row.testMethodOid, row.containerName);
    if (row.preservativeName) add(preservatives, row.testMethodOid, row.preservativeName);
  }
  return { containers, preservatives };
}

/** Storage locations (from Sample Location) → their storage condition.
 *  @deprecated Replaced by BottleLookups.storages from the API; kept as fallback. */
export const STORAGE_OPTS: readonly (readonly [string, string])[] = [
  ['', ''],
  ['FR-01', 'Refrigerated 2–8 °C'], ['FR-02', 'Refrigerated 2–8 °C'],
  ['FZ-01', 'Frozen −20 °C'], ['FZ-02', 'Frozen −80 °C'],
  ['AMB-01', 'Ambient 15–25 °C'], ['AMB-02', 'Ambient 15–25 °C'],
  ['ARCH', 'Archive'],
];

export function storageCondition(id: string): string {
  return STORAGE_OPTS.find(o => o[0] === id)?.[1] ?? '';
}

// ── Bottle / Container reference lookups (DB-driven) ─────────────────────

export interface BottleLookupOption { oid: string; name: string }

/** One row from the RS5 TestGuide map returned by the bottle-lookups API. */
export interface TestGuideLookup {
  testMethodOid: string;
  containerName: string | null;
  preservativeName: string | null;
}

export interface BottleLookups {
  containers: BottleLookupOption[];
  preservatives: BottleLookupOption[];
  storages: BottleLookupOption[];
  storageConditions: BottleLookupOption[];
  /** Per-TestMethod filter map from dbo.TestGuide (RS5). */
  testGuide: TestGuideLookup[];
}

/** Sample / test status dropdown (legacy ALPACA list; '' = not set). */
export const SAMPLE_STATUS_OPTS = [
  '', 'Instrument Down', 'Rejected', 'Out of service', 'Rejected/Recollection',
  'Sub Out', 'Not on the COCs', 'Hold',
];

/** Method → holding time (legacy rule, shown read-only on Test Assignment). */
function holdTime(name: string, method: string): string {
  if (name.startsWith('PFAS')) return '27d 22hr 29m';
  if (method === 'SM 9223B') return '30 hr';
  if (method === 'Field Method') return '15 min';
  if (method === 'EPA 300.0') return '48 hr';
  if (method === 'EPA 200.8') return '6 mo';
  return '';
}

/**
 * A test is a FIELD method when its method is the "Field Method" designation.
 * ONLY field methods let the Sample Login user key results here; lab methods get
 * their results in Result Entry, so their result cells stay read-only.
 */
export function isFieldTest(t: SrTest): boolean {
  return t.isFieldTest === true;
}

/** The Sample Login grid's default column set — [key, type, width].
 *  type: '' = text · number / date / time pickers · 'ro' = read-only ·
 *  'qty' = bottle count · 'status' = the sample-status dropdown.
 *  Legacy makes this configurable in Settings; this is the live default set. */
export const SL_COLS: readonly (readonly [string, string, number])[] = [
  ['SampleName', '', 130], ['SiteName', '', 120], ['SamplingAddress', '', 160],
  ['Qty', 'qty', 70], ['AlternativeStation', '', 130], ['DateCollected', 'date', 135],
  ['TimeCollected', 'time', 115], ['Collector', '', 110], ['ReceivedDate', 'ro', 130],
  ['SampleStatus', 'status', 130], ['SampleMatrix', 'ro', 110], ['CollectorPhone', '', 120],
  ['PWSID', '', 90], ['PWSSystemName', '', 130], ['ProjectNumber', '', 110], ['KeyMap', '', 90],
  ['SampleType', '', 100], ['WaterSource', '', 110], ['FreeColorine(mg/L)', 'number', 130],
  ['TotalChlorine(mg/L)', 'number', 130], ['RejectionCriteria', '', 125], ['RepeatLocation', '', 120],
  ['SamplingEvent', '', 110], ['EnteredBy', 'ro', 95], ['EnteredDate', 'ro', 105],
  ['Address', '', 130], ['SysSampleCode', '', 120], ['ParentSampleID', 'ro', 120],
  ['Remark', '', 120], ['Description', '', 130], ['PlannedLatitude', 'number', 120],
  ['PlannedLongitude', 'number', 125], ['QCCategory', '', 100],
];

/** Sample Condition Check points — verbatim from the live Check Points Setup,
 *  including the "coolor" spelling (preserve-the-quirk convention, §5.2). */
export const SCC_POINTS: readonly (readonly [string, string])[] = [
  ['Cooler seal present and signed', 'no'],
  ['Sample(s) in a cooler', 'yes'],
  ['If yes, ice in the coolor', 'yes'],
  ['Sample(s) received with chain-of-custody', 'yes'],
  ['C-O-C signed and dated', 'yes'],
  ['Sample(s) received with signed sample custody seal', 'no'],
  ['Sample containers arrived intact (if no comment)', 'yes'],
  ['Sample(s) were received in appropriate container with preservation for the required method(s)', 'yes'],
  ['All samples were logged and labeled', 'yes'],
  ['Sample ID labels match C-O-C IDs', 'yes'],
  ['Bottle count on C-O-C matches bottle found', 'yes'],
  ['Sample volume is sufficient for analysis requested', 'yes'],
  ['Samples were received within the holding time', 'yes'],
  ['VOA vials completely filled, no air in the head space', 'na'],
  ['Sample(s) accepted', 'yes'],
  ['pH of sample checked and as required by method', 'na'],
  ['Free chlorine of sample checked and <0.1mg/L', 'yes'],
];

/** Item Charge pricing option from dbo.ItemChargePricing. */
export interface IcPricingOption {
  oid: string;
  itemCode: string;
  itemName: string;
  unitPrice: number;
  npUnitPrice: number;
  unit?: string;
}

/** Entry Guidance — verbatim from the user manual's field table; surfaced as a
 *  hover tooltip on every General / Sample / Report label. */
export const GUIDANCE: Record<string, string> = {
  'Job ID': 'Autogenerated identifier, read only',
  'Received Date': 'Showing current datetime automatically, editable',
  'Received By': 'Login user, editable',
  'Created By': 'Login user, read only',
  'Client': 'Select a client from the dropdown; Allow to add a new one',
  'Client Address': 'Auto displaying after a client is selected',
  'Client Phone': 'Auto displaying if the phone number has been added to the client',
  'Client Contact': 'Select a contact from the dropdown',
  'Quote ID': 'Select a Quote ID from the dropdown, optional',
  'PO #': 'Enter a PO number, optional',
  'Payment Status': 'Select a payment from the dropdown',
  'Relinquished By': 'Select one or add a new one',
  'Date Collected': 'Enter collection date',
  'Time Collected': 'Enter collection time, optional',
  'Collector': 'Select a collector from the dropdown box',
  'Manifest Number': 'Enter a number, optional',
  'Project ID': 'Select one from the dropdown box',
  'Project Name': 'Auto displaying if it is linked to the selected Project ID, read only',
  'Project Location': 'Auto displaying if it is linked to the selected Project ID, read only',
  'Project Category': 'Select one from the dropdown box, optional',
  'Status': 'Showing the current progress, read only',
  'Sample Matrix': 'Select one or multiple matrices from the dropdown box',
  'Test': 'Select one or more tests under the current sample matrix',
  'No Of Samples': 'Enter number of samples',
  'Sample Category': 'Select a category from the dropdown box, optional',
  'Remark': 'Enter comment if needed',
  'TAT': 'Select a TAT from the dropdown box',
  'Due Date': 'The due date will be automatically displayed',
  'Report Template': 'Select a report template, optional',
  'Report Delivery Address': 'Enter the special report address, optional',
  'Report Delivery Method': 'Enter the report delivery method, optional',
};

// ─────────────────────────────────────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────────────────────────────────────

let keySeq = 0;
/** Client-side stable key for a grid row (never persisted). */
export function nextKey(prefix: string): string {
  keySeq += 1;
  return `${prefix}-${keySeq}`;
}

/** Build a fresh test assignment from the catalog. */
export function mkTest(name: string, method: string, comp?: string, added = '', testGroupName = '', fieldTest = false, testMethodOid?: string): SrTest {
  const d = testDefaults(name);
  const cat = TA_CATALOG.find(r => r[0] === name && (!comp || r[2] === comp))
    ?? TA_CATALOG.find(r => r[0] === name);
  return {
    key: nextKey('t'),
    name, method, comp: comp || cat?.[2] || 'Default',
    testGroupName,
    // Field tests get NO lab bottle (legacy AssignBottleAllocationToSamples skips
    // IsFieldTest tests).
    bottle: fieldTest ? '' : 'A',
    container: d.container, preserv: d.preserv, storage: '',
    bd: null, params: null, paramData: {},
    hdTime: holdTime(name, method),
    status: '', remark: false, subOut: false, added, hold: false,
    isFieldTest: fieldTest || undefined,
    testMethodOid: testMethodOid || undefined,
  };
}

/**
 * Structural copy that KEEPS the test's key — for immutable state updates.
 * Row identity must survive an edit, or every grid row would remount and the
 * current-record (▶) marker and any open picker would lose their target.
 */
export function copyTest(t: SrTest): SrTest {
  return {
    ...t,
    params: t.params ? { ...t.params } : null,
    paramData: Object.fromEntries(Object.entries(t.paramData).map(([k, v]) => [k, { ...v }])),
    bd: t.bd ? Object.fromEntries(Object.entries(t.bd).map(([k, v]) => [k, { ...v }])) : null,
  };
}

/** Copy as a NEW record (fresh key) — for genuine duplication: Copy Samples,
 *  Copy Tests to Samples, and seeding a new sample from the test template. A clone
 *  has no persisted allocation yet, so clear hasSavedBottle (else it would be
 *  wrongly protected from auto bottle assignment — round-cause B). */
export function cloneTest(t: SrTest): SrTest {
  return { ...copyTest(t), key: nextKey('t'), hasSavedBottle: undefined };
}

/**
 * Bottle defaulting — ALPACA parity (SampleRegistrationViewController.addnewtestbottles:
 * a newly added test's `BottleID = "A"`). AI does NOT auto-distribute bottles by
 * container/preservative: a new (unassigned) lab test gets bottle "A", and any bottle
 * the user has ALREADY assigned by hand (A / B / C via the BottlePicker) is preserved.
 * So adding a different-container test no longer auto-jumps it to "B" (user report
 * 2026-09-29) — the analyst distributes bottles themselves, exactly as legacy assigns
 * them only through the explicit "Assign bottles" action. Field tests are performed in
 * the field and get no lab bottle (legacy COC-import exclusion).
 *
 * Qty is the number of physical containers and is USER-OWNED, exactly as in ALPACA
 * (SampleRegistrationBottleAllocationViewController.btnAssignbottles:
 * `if (sampleReg.Qty == 0) sampleReg.Qty = bottleCount;`). It is auto-derived from the
 * distinct-bottle count ONLY while the user has never set it; once the user types a Qty
 * (marked by `v.__qtyManual`) adding a test / re-assigning bottles NEVER overwrites it.
 * `qtyIsManual` is the AI equivalent of legacy's `Qty != 0`.
 * Mutates and returns the sample.
 */
export function autoBottles(s: SrSample): SrSample {
  // Every non-field test defaults to bottle "A" — EXACT ALPACA parity
  // (SampleRegistrationViewController.addnewtestbottles sets `BottleID = "A"`
  // for every test, regardless of how many tests or the sample's Qty). Tests are
  // NOT auto-spread across A/B/C — that only happens through the explicit "Assign
  // Bottles" action. So the same test added twice (different components) shows
  // "A" and "A", not "A"/"B" (user report 2026-09-30: "ALPACA shows A for all the
  // tests, but ours added A,B,C"). A bottle the user hand-assigned via the
  // BottlePicker (A / B / C, or a multi-letter "A,B") is preserved; a field test
  // gets no lab bottle (legacy COC-import exclusion).
  for (const t of s.tests) {
    if (isFieldTest(t)) { t.bottle = ''; continue; }
    if (!t.bottle) t.bottle = 'A';   // default only; keep any hand-assigned A/B/C
  }

  if (!qtyIsManual(s)) syncQtyFromBottles(s);
  return s;
}

/** Set Qty from the distinct hand-/default-assigned bottle letters (never below 1).
 *  Shared by autoBottles and syncQty. */
function syncQtyFromBottles(s: SrSample): void {
  const set = new Set<string>();
  for (const t of s.tests) {
    if (isFieldTest(t)) continue;
    for (const b of (t.bottle || 'A').split(',')) { const x = b.trim(); if (x) set.add(x); }
  }
  s.v.Qty = String(Math.max(1, set.size));
}

/** True once the user has typed a Qty for this sample (legacy: `SampleLogIn.Qty != 0`);
 *  while true, autoBottles / syncQty leave Qty exactly as the user set it. */
export function qtyIsManual(s: SrSample): boolean {
  return s.v.__qtyManual === '1';
}

/** Mark this sample's Qty as user-owned so it is never auto-derived again (call from
 *  the Qty cell edit). */
export function markQtyManual(s: SrSample): void {
  s.v.__qtyManual = '1';
}

/** Clear the user-owned Qty marker so Qty auto-derives from the bottle count again
 *  (call from Reset Bottles / Reset Job). */
export function clearQtyManual(s: SrSample): void {
  delete s.v.__qtyManual;
}

/** Qty follows the distinct bottle count after a MANUAL bottle change — unless the
 *  user has set Qty themselves, in which case it is left untouched (legacy parity:
 *  Qty is the user's container count, independent of the bottle letters). */
export function syncQty(s: SrSample): void {
  if (qtyIsManual(s)) return;
  syncQtyFromBottles(s);
}

/**
 * Assign bottle letters to a sample's lab tests — ALPACA "Assign Bottles"
 * (SampleRegistrationBottleAllocationViewController.btnAssignbottles_BottleAllocation_Execute
 * + the per-matrix dbo.BottleSharing table). Tests whose TestMethod.Oid belongs to
 * the SAME sharing group — and there are >1 of them on this sample — SHARE one
 * bottle letter; every remaining lab test gets its own sequential letter A, B, C….
 * Field tests get no lab bottle. Qty is reset to the resulting distinct-bottle
 * count (legacy sets SampleRegistration.Qty to the bottle count). Mutates `s`.
 *
 * @param sharingGroups each inner array = TestMethod Oids that share a bottle,
 *   from GET /api/v1/sample-registration/bottle-sharing (empty = no sharing rules,
 *   so every test simply gets its own letter).
 */
export function assignBottleLetters(s: SrSample, sharingGroups: string[][]): void {
  const letter = (i: number) => String.fromCharCode(65 + Math.min(Math.max(0, i), 25));
  const labTests = s.tests.filter(t => !isFieldTest(t));
  // Field tests never get a lab bottle (legacy COC-import exclusion).
  for (const t of s.tests) if (isFieldTest(t)) t.bottle = '';

  const assigned = new Map<string, string>(); // test.key → letter
  let next = 0;

  // Shared groups first: a group with >1 of this sample's tests shares ONE letter.
  for (const group of sharingGroups) {
    const set = new Set(group.map(g => g.toLowerCase()));
    const inGroup = labTests.filter(
      t => t.testMethodOid && set.has(t.testMethodOid.toLowerCase()) && !assigned.has(t.key));
    if (inGroup.length > 1) {
      const l = letter(next++);
      for (const t of inGroup) assigned.set(t.key, l);
    }
  }
  // Remaining tests each get their own letter.
  for (const t of labTests) {
    if (!assigned.has(t.key)) assigned.set(t.key, letter(next++));
  }
  // Apply, realigning any per-bottle detail onto the new single letter.
  for (const t of labTests) {
    const l = assigned.get(t.key) ?? 'A';
    t.bottle = l;
    if (t.bd) {
      const first = Object.values(t.bd)[0];
      t.bd = first ? { [l]: { ...first } } : null;
    }
  }
  // Legacy btnAssignbottles only sets Qty when it is still 0 (`if (Qty == 0)`); it never
  // overwrites a Container Qty the user entered. So: a user-owned Qty is kept (raised only
  // if the assignment needs more bottles than the pool holds); otherwise Qty follows the
  // distinct bottle count.
  if (qtyIsManual(s)) {
    const before = Number.parseInt(s.v.Qty ?? '1', 10) || 1;
    syncQtyFromBottles(s);
    const needed = Number.parseInt(s.v.Qty ?? '1', 10) || 1;
    s.v.Qty = String(Math.max(before, needed));
  } else {
    syncQtyFromBottles(s);
  }
}

// NOTE: the old `normBottle` ("same bottle = one physical container") was removed —
// ALPACA keeps Container / Preservative / Storage PER TEST (per SampleBottleAllocation
// row), so two tests sharing a BottleID may have different containers/preservatives.
// Nothing normalises them together any more (user report 2026-09-29).

/** How many analytes a test currently includes, and its picker label. */
export function paramLabel(t: SrTest): string {
  const total = paramsOf(t.name).length;
  const n = t.params ? Object.values(t.params).filter(Boolean).length : total;
  return n === total ? 'AllParam' : `Custom (${n})`;
}

/** Analysis-charge rollup — qty per test = how many samples carry it. */
export interface SrChargeRow { test: string; method: string; qty: number; unitPrice: number; amount: number }

export function chargeRows(samples: SrSample[]): { rows: SrChargeRow[]; total: number } {
  const agg = new Map<string, { method: string; qty: number }>();
  for (const s of samples) {
    for (const t of s.tests) {
      const cur = agg.get(t.name) ?? { method: t.method, qty: 0 };
      cur.qty += 1;
      agg.set(t.name, cur);
    }
  }
  const rows = [...agg.entries()].map(([test, { method, qty }]) => {
    const priceCell = LOOKUPS.test.rows.find(r => r[0] === test)?.[2] ?? '$0.00';
    const unitPrice = Number.parseFloat(priceCell.replace('$', '')) || 0;
    return { test, method, qty, unitPrice, amount: unitPrice * qty };
  });
  return { rows, total: rows.reduce((a, r) => a + r.amount, 0) };
}

/** MM/DD/YYYY (or an ISO string) → yyyy-MM-dd for a native date input. */
export function toDateInput(v: string): string {
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const parts = v.split(' ')[0]?.split('/') ?? [];
  const [m, d, y] = parts;
  return m && d && y ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : '';
}

/** yyyy-MM-ddTHH:mm for a native datetime-local input. */
export function toDateTimeInput(v: string): string {
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 16);
  const [datePart, timePart] = v.split(' ');
  const iso = toDateInput(datePart ?? '');
  return iso ? `${iso}T${(timePart ?? '00:00').slice(0, 5)}` : '';
}

/** Add N calendar days to a yyyy-MM-dd / datetime-local value, returning yyyy-MM-dd. */
export function addDays(value: string, days: number): string {
  const iso = toDateInput(value);
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(y, m - 1, d + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

// ── Working-day / holiday-skipping due-date calc (mirrors SampleRegistrationRules.cs) ──

function isWorkingDay(dt: Date, holidays: ReadonlySet<string>): boolean {
  const dow = dt.getDay();
  if (dow === 0 || dow === 6) return false;
  const pad = (n: number) => String(n).padStart(2, '0');
  const key = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  return !holidays.has(key);
}

function addWorkingDays(dt: Date, daysToAdd: number, holidays: ReadonlySet<string>): Date {
  const r = new Date(dt.getTime());
  let remaining = daysToAdd;
  while (remaining > 0) {
    r.setDate(r.getDate() + 1);
    if (isWorkingDay(r, holidays)) remaining--;
  }
  return r;
}

function addWorkingHours(dt: Date, hoursToAdd: number, holidays: ReadonlySet<string>): Date {
  const r = new Date(dt.getTime());
  let remaining = hoursToAdd;
  while (remaining > 0) {
    r.setHours(r.getHours() + 1);
    if (isWorkingDay(r, holidays)) remaining--;
  }
  return r;
}

/** Resolve due date from received date + TAT hours, skipping weekends/holidays.
 *  Returns a datetime-local string (yyyy-MM-ddTHH:mm), or '' if not computable.
 *  Mirrors SampleRegistrationRules.ResolveDueDate — hours >= 24 uses working
 *  days, < 24 uses working hours. */
export function resolveDueDate(
  receivedDate: string,
  tatHours: number,
  isSampling: boolean,
  holidays: ReadonlySet<string>,
): string {
  if (isSampling) return '';
  if (!receivedDate || tatHours <= 0) return '';
  const dtl = toDateTimeInput(receivedDate);
  if (!dtl) return '';
  const parts = dtl.split('T');
  const dp = parts[0] ?? '';
  const tp = parts[1] ?? '00:00';
  const dParts = dp.split('-').map(Number);
  const tParts = tp.split(':').map(Number);
  const y = dParts[0] ?? 0, mo = dParts[1] ?? 0, da = dParts[2] ?? 0;
  const hh = tParts[0] ?? 0, mm = tParts[1] ?? 0;
  if (!y || !mo || !da) return '';
  const from = new Date(y, mo - 1, da, hh, mm);
  if (isNaN(from.getTime())) return '';

  const result = tatHours >= 24
    ? addWorkingDays(from, Math.floor(tatHours / 24), holidays)
    : addWorkingHours(from, tatHours, holidays);

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${result.getFullYear()}-${pad(result.getMonth() + 1)}-${pad(result.getDate())}T${pad(result.getHours())}:${pad(result.getMinutes())}`;
}

/** {JobID}-NNN sample id. */
export function sampleId(jobId: string, ordinal: number): string {
  return `${jobId || 'NEW'}-${String(ordinal).padStart(3, '0')}`;
}

/** Re-number every sample id so they stay sequential and contiguous. */
export function renumber(samples: SrSample[], jobId: string): void {
  samples.forEach((s, i) => { s.id = sampleId(jobId, i + 1); });
}

/** Duplicate parameter (analyte) across tests on the same sample.
 *  Legacy parity: ValidateDuplicateAnalysis (SampleRegistrationViewController line 24106). */
export interface DuplicateAnalysis {
  sampleId: string;
  paramName: string;
  testNames: string[];
}

/** Find parameters that appear in more than one test on the same sample.
 *  @param getParams  returns cached parameter names for a test (undefined if not cached yet). */
export function findDuplicateAnalyses(
  samples: SrSample[],
  getParams: (t: SrTest) => string[] | undefined,
): DuplicateAnalysis[] {
  const dupes: DuplicateAnalysis[] = [];
  for (const s of samples) {
    if (s.tests.length < 2) continue;
    const paramToTests = new Map<string, string[]>();
    for (const t of s.tests) {
      const names = getParams(t);
      if (!names) continue;
      const included = t.params
        ? names.filter(n => t.params![n] !== false)
        : names;
      for (const p of included) {
        const arr = paramToTests.get(p) ?? [];
        if (!arr.includes(t.name)) arr.push(t.name);
        paramToTests.set(p, arr);
      }
    }
    for (const [param, tests] of paramToTests) {
      if (tests.length > 1) {
        dupes.push({ sampleId: s.id, paramName: param, testNames: tests });
      }
    }
  }
  return dupes;
}
